import { isNil } from '@gfe/core/utils/shared-utils';
import {
  CalendarEvent,
  CalendarEventModification
} from '@gfe/features/components/visualization/calendar/models/calendar.models';

import { VisualizationModule } from '@core/models/data-visualization/data-visualization.model';
import { AuxiliaryDataBundle } from '@core/models/entities/entity-visualization-interface';
import { VisualizationBuilders } from '@core/models/entities/generic-entity';
import { EntityCfgOverrides } from '@core/models/entities/generic-entity-cfg';
import { GenericEntityRegistryService } from '@core/services/user-models-registry/generic-entity-registry.service';

import { SiteSchedule } from './site-schedule';
import {
  UserSiteV1Model,
  auxiliarySitesByUid,
  makeSiteScheduleEvent,
  makeSiteScheduleModification
} from './user-site-v1';

/** A site schedule served as an entity of its own, the same record as in its site's `opening_schedules`. */
export interface UserSiteScheduleV1Model extends SiteSchedule {
  site_uid: string;
}

export const SITE_SCHEDULE_ENTITY_TYPE = 'site_schedule_window';

const siteScheduleCfgOverrides: EntityCfgOverrides<UserSiteScheduleV1Model> = {
  extraVisualizations: new Set<VisualizationModule>(['calendar']),
  // The owning site carries the time zone that anchors the schedule's naive times, and the
  // compatible sites carry the colours the event is painted with.
  extraAuxEntities: [
    {
      type: 'site',
      versionName: 'UserSite',
      uidPaths: [
        'site_uid',
        'compatible_pickup_site_uids[*]',
        'compatible_delivery_site_uids[*]'
      ]
    }
  ]
};

/**
 * Every edit is saved through the site's schedule list, so a schedule is editable only when
 * its site resolved and still lists it.
 */
function editableThroughSite(
  raw: UserSiteScheduleV1Model,
  site: UserSiteV1Model | undefined
): boolean {
  return !isNil(site?.opening_schedules?.find((sch) => sch.uid === raw.uid));
}

const siteScheduleVisualizationBuilders: VisualizationBuilders<UserSiteScheduleV1Model> = {
  buildCalendarEvents: (
    raw: UserSiteScheduleV1Model,
    auxData?: AuxiliaryDataBundle
  ): CalendarEvent[] => {
    const auxiliarySites = auxiliarySitesByUid(auxData);
    const site = auxiliarySites[raw.site_uid];
    return [
      makeSiteScheduleEvent(
        site ?? { uid: raw.site_uid },
        raw,
        auxiliarySites,
        editableThroughSite(raw, site)
      )
    ];
  },

  buildCalendarModifications: (
    raw: UserSiteScheduleV1Model,
    auxData?: AuxiliaryDataBundle
  ): CalendarEventModification[] => {
    const site = auxiliarySitesByUid(auxData)[raw.site_uid];
    if (isNil(site) || !editableThroughSite(raw, site)) {
      return [];
    }
    return [
      {
        ...makeSiteScheduleModification(site, raw),
        refreshEntityType: SITE_SCHEDULE_ENTITY_TYPE
      }
    ];
  }
};

export function registerSiteScheduleExtensions(
  registry: GenericEntityRegistryService
): void {
  registry.registerOverrides('UserSiteSchedule', siteScheduleCfgOverrides);
  registry.registerVisualizationBuilders(
    'UserSiteSchedule',
    siteScheduleVisualizationBuilders
  );
}
